import '@hello-pimster/pimster-ui/styles';

import { ApolloProvider } from '@apollo/client';
import AnimationLayout from '@components/layouts/animationLayout';
import { ContextualModal } from '@components/modals/custom/ContextualModal';
import { PreviewActionsButton } from '@components/preview/PreviewButton';
import { EditProvider } from '@components/utils/contexts/EditContext';
import { ModalProvider } from '@components/utils/contexts/modals/modalsContext';
import { ProductNavigationProvider } from '@components/utils/contexts/ProductNavigationContext';
import { TriggerEventProvider } from '@components/utils/contexts/TriggerEventContext';
import { UserProvider } from '@components/utils/contexts/UserContext';
import InitRouter from '@components/utils/router/initRouter';
import { TrackEvent } from '@enums';
import { useApollo } from '@lib//apollo/apollo';
import { AnalyticsProvider } from '@lib/analytics/analyticsContext';
import {
    PERFORMANCE_SAMPLING_RATE,
    PERFORMANCE_MAX_RETRY,
    PERFORMANCE_RETRY_DELAY_IN_MS,
} from '@lib/analytics/constants';
import { EmbedProvider } from '@lib/embed/providers/EmbedProvider';
import ApiCrmProvider from '@lib/pimster-crm/provider/ApiCrmProvider';
import { InitSentry } from '@lib/sentry/InitSentry';
import { ThemeProvider } from '@lib/uikit';
import * as Sentry from '@sentry/nextjs';
import { PageRequiredProps } from '@types';
import { getLocaleDirection } from '@utils/locale/direction';
import { MotionConfig } from 'framer-motion';
import parseHTML from 'html-react-parser';
import { AppProps, NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';
import { useEffect } from 'react';

import nextI18NextConfig from '../next-i18next.config';

const ToastPortal = dynamic(() => import('@components/toasts/ToastPortal'), {
    ssr: false,
});

const MyApp = ({
    Component,
    pageProps,
}: AppProps<Partial<PageRequiredProps>>) => {
    const { __APOLLO_STATE__, isPreview, serverCompanyContext } = pageProps;

    const companyConfig = serverCompanyContext?.config;

    const apolloClient = useApollo(isPreview ?? false, __APOLLO_STATE__ ?? {});

    const {
        locale,
        query: { company, product },
    } = useRouter();

    useEffect(() => {
        const htmlNode = document.querySelector('html');

        if (htmlNode) {
            htmlNode.setAttribute('dir', getLocaleDirection(locale));
        }

        Sentry.setTag('locale', locale);
    }, [locale]);

    useEffect(() => {
        Sentry.setTag(
            'company',
            Array.isArray(company) ? company.at(0) : company
        );
        Sentry.setTag(
            'product',
            Array.isArray(product) ? product.at(0) : product
        );
    }, [company, product]);

    return (
        <>
            <Head>
                <meta
                    name='last-modified'
                    content={new Date().toUTCString().slice(0, -4)}
                />
                {companyConfig &&
                    companyConfig?.companyCustomHead !== '' &&
                    parseHTML(companyConfig?.companyCustomHead)}
            </Head>
            <ThemeProvider companyTheme={companyConfig?.companyTheme}>
                <ApolloProvider client={apolloClient}>
                    <EditProvider>
                        <AnalyticsProvider>
                            <InitSentry />
                            <EmbedProvider>
                                <ApiCrmProvider>
                                    <ProductNavigationProvider>
                                        <TriggerEventProvider>
                                            <ModalProvider>
                                                <ContextualModal />
                                            </ModalProvider>
                                            <InitRouter />
                                            <ToastPortal />
                                            <PreviewActionsButton
                                                isPreview={isPreview}
                                            />
                                            <UserProvider>
                                                <MotionConfig reducedMotion='user'>
                                                    <AnimationLayout>
                                                        <Component
                                                            {...pageProps}
                                                        />
                                                    </AnimationLayout>
                                                </MotionConfig>
                                            </UserProvider>
                                        </TriggerEventProvider>
                                    </ProductNavigationProvider>
                                </ApiCrmProvider>
                            </EmbedProvider>
                        </AnalyticsProvider>
                    </EditProvider>
                </ApolloProvider>
            </ThemeProvider>
            <Script
                id='app-script'
                strategy='afterInteractive'
                dangerouslySetInnerHTML={{
                    __html: `(function(h,o,t,j,a,r){
                                    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                                    h._hjSettings={hjid:2622559,hjsv:6};
                                    a=o.getElementsByTagName('head')[0];
                                    r=o.createElement('script');r.async=1;
                                    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                                    a.appendChild(r);
                            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
                }}
            />
        </>
    );
};

export function reportWebVitals({
    id,
    name,
    label,
    value,
}: NextWebVitalsMetric) {
    const randomNumber = Math.random();
    if (randomNumber < PERFORMANCE_SAMPLING_RATE) {
        const trackPerformanceEvent = () => {
            // @ts-ignore
            window.analytics.track(TrackEvent.PerformanceMetric, {
                name,
                event_category:
                    label === 'web-vital'
                        ? 'Web Vitals'
                        : 'Next.js custom metric',
                value: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
                event_label: id, // id unique to current page load
                non_interaction: true,
            });
        };

        let retryCount = 0;
        const interval = setInterval(() => {
            // @ts-ignore
            if (window.analytics) {
                clearInterval(interval);
                trackPerformanceEvent();
            } else {
                retryCount++;
                if (retryCount >= PERFORMANCE_MAX_RETRY) {
                    console.warn(
                        `Performance metric couldn't be sent after  ${PERFORMANCE_MAX_RETRY} retries`
                    );
                    clearInterval(interval);
                }
            }
        }, PERFORMANCE_RETRY_DELAY_IN_MS);
    }
}

//  @ts-ignore : Error exist because next-i18n.config is not typed
export default appWithTranslation(MyApp, nextI18NextConfig);
